import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import {
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import moment from "moment";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import {
  getDigest,
  getEventos,
  getResumo,
} from "../../services/monitoramentoSerasa";
import type {
  EventoMonitoramento,
  MonitoramentoDigest,
  MonitoramentoResumo,
  PaginaEventos,
} from "../../models/monitoramentoSerasa/types";
import { normalizeCnpjParaApi } from "./cnpj";
import {
  ApiErrorPanel,
  formatBRL,
  formatDataHora,
  KpiCard,
  PainelRodape,
  corBordaSeveridadeMui,
  severidadeComoTexto,
} from "./shared";

function eventosParaLista(data: PaginaEventos): EventoMonitoramento[] {
  return data?.itens ?? [];
}

/** Chaves do `distribuicaoSegmento` (ex.: `Critico`, `Saudavel`). */
function corSegmentoPorChave(nomeChave: string): string {
  const k = nomeChave.toLowerCase();
  if (k.includes("saud")) return "var(--success)";
  if (k.includes("tenc") || k.includes("aten")) return "var(--warning)";
  if (k.includes("crit")) return "var(--error)";
  return "var(--text-secondary)";
}

export const MonitoramentoHome = () => {
  const [digest, setDigest] = useState<MonitoramentoDigest | null>(null);
  const [resumo, setResumo] = useState<MonitoramentoResumo | null>(null);
  const [eventos, setEventos] = useState<EventoMonitoramento[]>([]);
  /** Intervalo do mês calendário usado no digest (rótulo alinhado às contagens mensais). */
  const [periodoDigestMensal, setPeriodoDigestMensal] = useState<{
    inicioIso: string;
    fimIso: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro("");
    const desdeUtc = moment().subtract(30, "days").toISOString();
    const ateUtc = moment().toISOString();
    const agora = moment();
    const inicioMesLocal = agora.clone().startOf("month");
    const fimMesLocal = agora.clone().endOf("month");
    const dataReferenciaUtc = inicioMesLocal.toISOString();
    try {
      const [d, r, ev] = await Promise.all([
        getDigest({ dataReferenciaUtc }),
        getResumo(),
        getEventos({ desdeUtc, ateUtc, pagina: 1, tamanhoPagina: 20 }),
      ]);
      setDigest(d);
      setResumo(r);
      setEventos(eventosParaLista(ev).slice(0, 8));
      setPeriodoDigestMensal({
        inicioIso: inicioMesLocal.toISOString(),
        fimIso: fimMesLocal.toISOString(),
      });
    } catch (e: unknown) {
      const msg =
        e && typeof e === "object" && "message" in e
          ? String((e as { message?: string }).message)
          : "Não foi possível carregar o painel.";
      setErro(msg);
      setPeriodoDigestMensal(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const mixCarteira = useMemo(() => {
    const rec = resumo?.distribuicaoSegmento;
    if (!rec) return [];
    return Object.entries(rec).map(([chave, qtd]) => ({
      name: chave,
      value: qtd ?? 0,
      fill: corSegmentoPorChave(chave),
    }));
  }, [resumo]);

  const totalDonut = useMemo(() => {
    if (resumo?.totalCnpjsMonitorados != null) return resumo.totalCnpjsMonitorados;
    return mixCarteira.reduce((a, b) => a + (b.value || 0), 0);
  }, [resumo, mixCarteira]);

  const labelRodapeDados =
    digest?.geradoEmUtc != null || resumo?.geradoEmUtc != null
      ? `Dados até ${formatDataHora(digest?.geradoEmUtc || resumo?.geradoEmUtc)}`
      : undefined;

  const subtituloPeriodo = periodoDigestMensal
    ? `Período analisado (mensal): ${formatDataHora(periodoDigestMensal.inicioIso)} — ${formatDataHora(
        periodoDigestMensal.fimIso
      )}`
    : null;

  const linkFornecedor = (cnpj?: string) => {
    const n = normalizeCnpjParaApi(cnpj);
    if (!n) return "#";
    return `/monitoramento/fornecedor/${n}`;
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h2" fontWeight={700} gutterBottom>
          Monitoramento da carteira
        </Typography>
        <Typography color="text.secondary" variant="subtitle1">
          {subtituloPeriodo ||
            "Visão consolidada do acompanhamento Serasa (digest + resumo + eventos)."}
        </Typography>
      </Box>

      {erro ? (
        <Box sx={{ mb: 2 }}>
          <ApiErrorPanel message={erro} onRetry={carregar} />
        </Box>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <KpiCard
            titulo="Pioraram no mês"
            loading={loading}
            valor={digest?.quantidadePioraram ?? "—"}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <KpiCard
            titulo="Melhoraram no mês"
            loading={loading}
            valor={digest?.quantidadeMelhoraram ?? "—"}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <KpiCard
            titulo="Soma anotações (carteira)"
            loading={loading}
            valor={formatBRL(resumo?.somaValorTotalAnotacoes)}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={5}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Mix da carteira
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Por segmento (Saudável / Atenção / Crítico)
              </Typography>
              <Box sx={{ width: "100%", height: 280 }}>
                {loading ? (
                  <Typography color="text.secondary">Carregando…</Typography>
                ) : mixCarteira.length === 0 ? (
                  <Typography color="text.secondary">
                    Sem <code>distribuicaoSegmento</code> no resumo.
                  </Typography>
                ) : (
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={mixCarteira}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={68}
                        outerRadius={100}
                        paddingAngle={2}
                      />
                      <Tooltip formatter={(v: any) => [v ?? 0, "CNPJs"]} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </Box>
              <Typography align="center" variant="body2" color="text.secondary">
                Total monitorados:{" "}
                <strong>{totalDonut || "—"}</strong> CNPJs
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={7}>
          <Card variant="outlined" sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Resumo agregado
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Totais do endpoint <code>resumo</code> (sem série temporal na home)
              </Typography>
              <Box sx={{ width: "100%", minHeight: 220 }}>
                {loading ? (
                  <Typography color="text.secondary">Carregando…</Typography>
                ) : (
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    <Typography component="li" variant="body2">
                      CNPJs monitorados: <strong>{resumo?.totalCnpjsMonitorados ?? "—"}</strong>
                    </Typography>
                    <Typography component="li" variant="body2">
                      Soma valor protestos:{" "}
                      <strong>{formatBRL(resumo?.somaValorProtestos)}</strong>
                    </Typography>
                    <Typography component="li" variant="body2">
                      Qtd. protestos (total):{" "}
                      <strong>{resumo?.totalQuantidadeProtestos ?? "—"}</strong>
                    </Typography>
                    <Typography component="li" variant="body2">
                      CNPJs com protesto:{" "}
                      <strong>{resumo?.cnpjsComProtesto ?? "—"}</strong>
                    </Typography>
                    <Typography component="li" variant="body2">
                      CNPJs com ação judicial:{" "}
                      <strong>{resumo?.cnpjsComAcaoJudicial ?? "—"}</strong>
                    </Typography>
                    {digest?.quantidadeEstaveis != null ? (
                      <Typography component="li" variant="body2" sx={{ mt: 1 }}>
                        Estáveis no mês (digest): <strong>{digest.quantidadeEstaveis}</strong>
                      </Typography>
                    ) : null}
                  </Box>
                )}
                {(digest?.insights?.length ?? 0) > 0 ? (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Insights
                    </Typography>
                    {(digest?.insights ?? []).map((t, i) => (
                      <Typography key={i} variant="body2" color="text.secondary" display="block">
                        • {t}
                      </Typography>
                    ))}
                  </Box>
                ) : null}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Top mudanças negativas
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Digest mensal (mesmo período dos KPIs acima).
              </Typography>
              <List dense disablePadding>
                {(digest?.topMudancasNegativas ?? []).slice(0, 8).map((m, i) => (
                  <ListItem key={`n-${i}`} disableGutters sx={{ py: 0.5 }}>
                    <ListItemText
                      primary={
                        <Link
                          component={RouterLink}
                          to={linkFornecedor(m.cnpj)}
                          underline="hover"
                        >
                          {m.razaoSocial || m.cnpj || "Fornecedor"}
                        </Link>
                      }
                      secondary={
                        <span>
                          {m.indicadorPrincipal ?? "—"}{" "}
                          {m.variacaoValorTotalAnotacoes != null
                            ? `(${formatBRL(m.variacaoValorTotalAnotacoes)}) `
                            : ""}
                          {severidadeComoTexto(m.severidade) ? (
                            <Chip
                              size="small"
                              label={String(severidadeComoTexto(m.severidade))}
                              color="error"
                              sx={{ ml: 0.5, verticalAlign: "middle" }}
                            />
                          ) : null}
                        </span>
                      }
                    />
                  </ListItem>
                ))}
                {!loading &&
                (digest?.topMudancasNegativas == null ||
                  digest.topMudancasNegativas.length === 0) ? (
                  <Typography variant="body2" color="text.secondary">
                    Nenhuma mudança negativa listada neste mês.
                  </Typography>
                ) : null}
              </List>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Top mudanças positivas
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Digest mensal (mesmo período dos KPIs acima).
              </Typography>
              <List dense disablePadding>
                {(digest?.topMudancasPositivas ?? []).slice(0, 8).map((m, i) => (
                  <ListItem key={`p-${i}`} disableGutters sx={{ py: 0.5 }}>
                    <ListItemText
                      primary={
                        <Link
                          component={RouterLink}
                          to={linkFornecedor(m.cnpj)}
                          underline="hover"
                        >
                          {m.razaoSocial || m.cnpj || "Fornecedor"}
                        </Link>
                      }
                      secondary={
                        <span>
                          {m.indicadorPrincipal ?? "—"}{" "}
                          {m.variacaoValorTotalAnotacoes != null
                            ? `(${formatBRL(m.variacaoValorTotalAnotacoes)}) `
                            : ""}
                          {severidadeComoTexto(m.severidade) ? (
                            <Chip
                              size="small"
                              label={String(severidadeComoTexto(m.severidade))}
                              color="success"
                              sx={{ ml: 0.5, verticalAlign: "middle" }}
                            />
                          ) : null}
                        </span>
                      }
                    />
                  </ListItem>
                ))}
                {!loading &&
                (digest?.topMudancasPositivas == null ||
                  digest.topMudancasPositivas.length === 0) ? (
                  <Typography variant="body2" color="text.secondary">
                    Nenhuma mudança positiva listada neste mês.
                  </Typography>
                ) : null}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Eventos recentes
              </Typography>
              <List dense>
                {eventos.map((ev, idx) => (
                  <ListItem
                    key={`${ev.cnpj}-${ev.dataConsulta}-${ev.tipoEvento}-${idx}`}
                    sx={{
                      borderLeft: 4,
                      borderColor: corBordaSeveridadeMui(ev.severidade),
                      mb: 1,
                      bgcolor: "background.paper",
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <NotificationsActiveOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={ev.mensagemCurta || ev.tipoEvento || "Evento"}
                      secondary={formatDataHora(ev.dataConsulta)}
                    />
                    {normalizeCnpjParaApi(ev.cnpj) ? (
                      <Button
                        component={RouterLink}
                        to={linkFornecedor(ev.cnpj)}
                        size="small"
                        variant="outlined"
                      >
                        Ver fornecedor
                      </Button>
                    ) : null}
                  </ListItem>
                ))}
                {!loading && eventos.length === 0 ? (
                  <Typography variant="body2" color="success.main">
                    Nenhum evento recente — bom sinal para a carteira.
                  </Typography>
                ) : null}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <PainelRodape
        versaoRegras={digest?.versaoRegras ?? resumo?.versaoRegras}
        labelDadosAte={labelRodapeDados}
      />
    </Box>
  );
};
